"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import HomeSection from "@/components/marketing/HomeSection";
import { useTranslation } from "react-i18next";

type AnswerBlock =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] };

function formatAnswer(answer: string): AnswerBlock[] {
  const blocks: AnswerBlock[] = [];

  for (const line of answer.split(/\r?\n/)) {
    const text = line.trim();
    if (!text) continue;

    if (text.startsWith("- ")) {
      const previous = blocks[blocks.length - 1];
      if (previous?.type === "list") {
        previous.items.push(text.slice(2));
      } else {
        blocks.push({ type: "list", items: [text.slice(2)] });
      }
    } else {
      blocks.push({ type: "paragraph", text });
    }
  }

  return blocks;
}

export const FAQ = () => {
  const { t } = useTranslation();

  const order = [1, 9, 10, 2, 3, 4, 5, 6, 7, 11, 8];
  const items = order.map((n) => ({
    q: t(`FAQ_Q${n}_QUESTION_TEXT` as const),
    a: t(`FAQ_Q${n}_ANSWER_TEXT` as const),
  }));
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.a,
      },
    })),
  } as const;

  return (
    <section
      className="w-full scroll-mt-24 py-16 sm:py-20"
      id="faq"
      aria-labelledby="faq-title"
    >
      <script
        id="faq-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd).replace(/</g, "\\u003c") }}
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-10 rounded-[2rem] border border-white/60 bg-white/65 px-6 py-9 shadow-[0_30px_100px_-70px_rgba(15,23,42,0.45)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.06] sm:px-10 sm:py-12 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] lg:gap-14 lg:px-12 lg:py-14">
          <div className="lg:sticky lg:top-32">
            <HomeSection
              badgeText="FAQ_BADGE_TEXT"
              title="FAQ_TITLE_TEXT"
              subtitle="FAQ_SUBTITLE_TEXT"
              titleId="faq-title"
              titleSize="compact"
              titleStyle="editorial"
              align="left"
            />
          </div>

          <div className="min-w-0">
            <Accordion type="single" collapsible className="w-full divide-y divide-slate-200/80 overflow-hidden rounded-2xl border border-slate-200/80 bg-white/75 shadow-sm dark:divide-white/10 dark:border-white/10 dark:bg-slate-900/40">
              {items.map((faq, index) => (
                <AccordionItem key={index} value={"index-" + index} className="border-none transition-colors data-[state=open]:bg-indigo-50/45 dark:data-[state=open]:bg-white/[0.04]">
                  <AccordionTrigger className="gap-4 px-5 py-5 text-left text-base font-semibold leading-snug text-slate-900 hover:bg-slate-50/70 hover:text-indigo-700 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 data-[state=open]:text-indigo-700 dark:text-white dark:hover:bg-white/[0.04] dark:hover:text-sky-200 dark:data-[state=open]:text-sky-200 sm:px-6">
                    <span>{faq.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 sm:px-6">
                    <div className="space-y-3 text-sm leading-6 text-slate-600 dark:text-slate-300 sm:text-base sm:leading-7">
                      {formatAnswer(faq.a).map((block, blockIndex) =>
                        block.type === "list" ? (
                          <ul
                            key={blockIndex}
                            className="list-disc space-y-2 pl-6 marker:text-indigo-500 dark:marker:text-sky-300"
                          >
                            {block.items.map((item, itemIndex) => (
                              <li key={itemIndex} className="pl-1">{item}</li>
                            ))}
                          </ul>
                        ) : (
                          <p key={blockIndex}>{block.text}</p>
                        ),
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};
